import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Box, Button, Stack } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HighlightAltIcon from "@mui/icons-material/HighlightAlt";
import UndoIcon from "@mui/icons-material/Undo";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const PEN = { tool: "pen", color: "#d23a2a", width: 4 };
const HIGHLIGHT = { tool: "highlight", color: "rgba(201, 161, 75, 0.45)", width: 28 };
const MAX_PREVIEW_HEIGHT = 360;

export function fittedDisplaySize(intrinsicWidth, intrinsicHeight, maxWidth, maxHeight) {
  if (!intrinsicWidth || !intrinsicHeight || !maxWidth || !maxHeight) {
    return { width: intrinsicWidth || 1, height: intrinsicHeight || 1 };
  }
  const scale = Math.min(maxWidth / intrinsicWidth, maxHeight / intrinsicHeight);
  return {
    width: Math.max(1, Math.round(intrinsicWidth * scale)),
    height: Math.max(1, Math.round(intrinsicHeight * scale)),
  };
}

export function contentPoint(clientX, clientY, rect, border, canvasWidth, canvasHeight) {
  const contentWidth = Math.max(1, rect.width - border.left - border.right);
  const contentHeight = Math.max(1, rect.height - border.top - border.bottom);
  return {
    x: ((clientX - rect.left - border.left) * canvasWidth) / contentWidth,
    y: ((clientY - rect.top - border.top) * canvasHeight) / contentHeight,
  };
}

function strokeStyle(stroke) {
  return stroke.tool === "highlight" ? HIGHLIGHT : PEN;
}

function draw(canvas, image, strokes) {
  const ctx = canvas.getContext("2d");
  if (!ctx || !image) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  strokes.forEach((stroke) => {
    if (!stroke.points.length) return;
    const style = strokeStyle(stroke);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = style.color;
    ctx.lineWidth = style.width;
    ctx.beginPath();
    stroke.points.forEach((point, index) => {
      if (index === 0) ctx.moveTo(point.x, point.y);
      else ctx.lineTo(point.x, point.y);
    });
    ctx.stroke();
  });
}

function applyDisplaySize(canvas, frame, image) {
  if (!canvas || !frame || !image) return;
  const maxWidth = frame.clientWidth;
  if (!maxWidth) return;
  const size = fittedDisplaySize(
    image.naturalWidth || image.width,
    image.naturalHeight || image.height,
    maxWidth,
    MAX_PREVIEW_HEIGHT
  );
  canvas.style.width = `${size.width}px`;
  canvas.style.height = `${size.height}px`;
}

function eventPoint(event, canvas) {
  const rect = canvas.getBoundingClientRect();
  const style = window.getComputedStyle(canvas);
  const border = {
    left: parseFloat(style.borderLeftWidth) || 0,
    top: parseFloat(style.borderTopWidth) || 0,
    right: parseFloat(style.borderRightWidth) || 0,
    bottom: parseFloat(style.borderBottomWidth) || 0,
  };
  return contentPoint(event.clientX, event.clientY, rect, border, canvas.width, canvas.height);
}

const ScreenshotAnnotator = forwardRef(function ScreenshotAnnotator({ blob }, ref) {
  const canvasRef = useRef(null);
  const frameRef = useRef(null);
  const imageRef = useRef(null);
  const drawing = useRef(null);
  const [tool, setTool] = useState("pen");
  const [strokes, setStrokes] = useState([]);

  useImperativeHandle(ref, () => ({
    exportJpeg() {
      return new Promise((resolve) => {
        const canvas = canvasRef.current;
        if (!canvas || !imageRef.current) {
          resolve(blob);
          return;
        }
        canvas.toBlob((next) => resolve(next || blob), "image/jpeg", 0.7);
      });
    },
  }));

  useEffect(() => {
    if (!blob) return undefined;
    let cancelled = false;
    let objectUrl = URL.createObjectURL(blob);
    const image = new Image();
    const releaseUrl = () => {
      if (!objectUrl) return;
      URL.revokeObjectURL(objectUrl);
      objectUrl = null;
    };
    image.onload = () => {
      releaseUrl();
      if (cancelled) return;
      imageRef.current = image;
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = image.naturalWidth || image.width;
      canvas.height = image.naturalHeight || image.height;
      applyDisplaySize(canvas, frameRef.current, image);
      setStrokes([]);
      draw(canvas, image, []);
    };
    image.onerror = () => {
      releaseUrl();
    };
    image.src = objectUrl;
    return () => {
      cancelled = true;
    };
  }, [blob]);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => {
      applyDisplaySize(canvasRef.current, frameRef.current, imageRef.current);
    });
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imageRef.current) return;
    draw(canvas, imageRef.current, strokes);
  }, [strokes]);

  const onPointerDown = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(event.pointerId);
    drawing.current = { tool, points: [eventPoint(event, canvas)] };
  };

  const onPointerMove = (event) => {
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    drawing.current.points.push(eventPoint(event, canvas));
    draw(canvas, imageRef.current, [...strokes, drawing.current]);
  };

  const onPointerUp = () => {
    if (!drawing.current) return;
    const next = [...strokes, drawing.current];
    drawing.current = null;
    setStrokes(next);
  };

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
        <Button
          size="small"
          variant={tool === "pen" ? "contained" : "outlined"}
          startIcon={<EditOutlinedIcon />}
          onClick={() => setTool("pen")}
        >
          Pen
        </Button>
        <Button
          size="small"
          variant={tool === "highlight" ? "contained" : "outlined"}
          startIcon={<HighlightAltIcon />}
          onClick={() => setTool("highlight")}
        >
          Highlight
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<UndoIcon />}
          disabled={!strokes.length}
          onClick={() => setStrokes((current) => current.slice(0, -1))}
        >
          Undo
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<DeleteOutlineIcon />}
          disabled={!strokes.length}
          onClick={() => setStrokes([])}
        >
          Clear
        </Button>
      </Stack>
      <Box ref={frameRef} sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
        <Box
          component="canvas"
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={() => {
            if (!drawing.current) return;
            onPointerUp();
          }}
          sx={{
            display: "block",
            boxSizing: "content-box",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            cursor: "crosshair",
            touchAction: "none",
            backgroundColor: "background.paper",
          }}
        />
      </Box>
    </Stack>
  );
});

export default ScreenshotAnnotator;
